import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { useDesk } from './desk';
import type {
  Assignee,
  AutomationRunStatus,
  Condition,
  ConditionGroup,
  ConditionValue,
  Department,
  DepartmentAudience,
  DurationUnit,
  NotifyRecipient,
  RecurrenceFrequency,
  SmartRuleSource,
  WorkflowAction,
  WorkflowActionType,
  WorkflowListItem,
  WorkflowTrigger,
  WorkOrderSource,
} from './types';

// ---------------------------------------------------------------------------
// Triggers
// ---------------------------------------------------------------------------

export const TRIGGER_LABEL: Record<WorkflowTrigger, string> = {
  TICKET_CREATED: 'A ticket is opened',
  TICKET_UPDATED: 'A ticket is updated',
  COMMENT_ADDED: 'Someone comments',
  UNANSWERED: 'A ticket goes unanswered',
  STALE: 'A ticket goes quiet',
  UNRESOLVED: 'A ticket stays open',
};

export const TRIGGER_DESCRIPTION: Record<WorkflowTrigger, string> = {
  TICKET_CREATED: 'As soon as a ticket is submitted, logged by staff, or opened by a smart rule or schedule.',
  TICKET_UPDATED: 'When status, priority, assignee, department, or other details change.',
  COMMENT_ADDED: 'When the requester or the team adds a comment or note.',
  UNANSWERED: 'Once, when nobody on the team has responded within the time you set.',
  STALE: 'When a ticket has had no activity for the time you set, and again each time it goes quiet.',
  UNRESOLVED: 'Once, when a ticket is still open after the time you set (again if it’s reopened).',
};

export const TIME_TRIGGERS: WorkflowTrigger[] = ['UNANSWERED', 'STALE', 'UNRESOLVED'];

export const UPDATE_FIELD_LABEL: Record<string, string> = {
  status: 'Status',
  priority: 'Priority',
  category: 'Type of problem',
  assignee: 'Assignee',
  department: 'Department',
  repairNeeded: 'Repair flag',
  title: 'Summary',
  resolution: 'Resolution',
  escalation: 'Escalation',
};

export const DURATION_UNIT_LABEL: Record<DurationUnit, string> = { minutes: 'minutes', hours: 'hours', days: 'days' };

export function describeDuration(duration: { amount?: number; unit?: DurationUnit; businessHours?: boolean }) {
  const amount = duration.amount ?? 0;
  const unit = duration.unit ?? 'hours';
  const noun = amount === 1 ? unit.slice(0, -1) : unit;
  return `${amount} ${duration.businessHours ? `business ${noun}` : noun}`;
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

export const SOURCE_LABEL: Record<WorkOrderSource, string> = {
  REQUESTER: 'Requester',
  EMAIL: 'Email',
  STAFF: 'Technology team',
  SMART_RULE: 'Smart rule',
  RECURRING: 'Schedule',
  PREVENTIVE: 'Preventive maintenance',
  INSPECTION: 'Inspection follow-up',
};

export const AUDIENCE_LABEL: Record<DepartmentAudience, string> = {
  EVERYONE: 'Everyone',
  STUDENTS: 'Students',
  STAFF: 'Teachers & staff',
  ADMIN_ONLY: 'Technology team only',
  CUSTOM: 'Specific groups',
};

export const AUDIENCE_DESCRIPTION: Record<DepartmentAudience, string> = {
  EVERYONE: 'Anyone who submits tickets can choose it',
  STUDENTS: 'Only students can choose it',
  STAFF: 'Only teachers and staff can choose it',
  ADMIN_ONLY: 'Hidden from requesters; only the technology team routes tickets here',
  CUSTOM: 'Only the person types you pick can choose it',
};

export const RUN_STATUS_LABEL: Record<AutomationRunStatus, string> = {
  RUNNING: 'Running',
  SUCCEEDED: 'Succeeded',
  SKIPPED: 'Skipped',
  FAILED: 'Failed',
  WAITING: 'Waiting',
  CANCELED: 'Canceled',
};

export const RUN_TRIGGER_LABEL: Record<string, string> = {
  ...TRIGGER_LABEL,
  CHAINED: 'Run by another workflow',
  SCHEDULE: 'On schedule',
  MANUAL: 'Run manually',
};

export const SMART_SOURCE_LABEL: Record<SmartRuleSource, string> = {
  DEVICE_OFFLINE: 'A device stops checking in',
  AUTO_UPDATE_EXPIRING: 'Chromebook updates are ending',
  WARRANTY_EXPIRING: 'A warranty is ending',
  CHECKOUT_OVERDUE: 'A checkout is overdue',
  REPEAT_REPAIRS: 'A device keeps needing repairs',
  LOW_STOCK_PARTS: 'A part is running low',
  NETWORK_DEVICE_OFFLINE: 'Network equipment goes offline',
  DEPARTED_WITH_DEVICE: 'Someone left and still has a device',
};

export const SMART_SOURCE_PLACEHOLDERS: Record<SmartRuleSource, string[]> = {
  DEVICE_OFFLINE: [
    'asset.tag',
    'asset.name',
    'asset.assignee',
    'device.serial',
    'device.model',
    'device.name',
    'device.lastSync',
    'device.daysOffline',
    'device.user',
    'device.orgUnit',
  ],
  AUTO_UPDATE_EXPIRING: ['asset.tag', 'asset.name', 'asset.assignee', 'device.serial', 'device.model', 'device.aue', 'device.orgUnit'],
  WARRANTY_EXPIRING: ['asset.tag', 'asset.name', 'asset.model', 'asset.warrantyExpires', 'asset.assignee'],
  CHECKOUT_OVERDUE: ['asset.tag', 'asset.name', 'checkout.assignee', 'checkout.due', 'checkout.daysOverdue'],
  REPEAT_REPAIRS: ['asset.tag', 'asset.name', 'asset.model', 'tickets.count', 'tickets.days'],
  LOW_STOCK_PARTS: ['part.name', 'part.number', 'part.onHand', 'part.lowStockAt'],
  DEPARTED_WITH_DEVICE: [
    'person.name',
    'person.email',
    'person.type',
    'person.status',
    'person.gradYear',
    'person.leftOn',
    'devices.count',
    'devices.overdue',
    'devices.list',
    'devices.tags',
  ],
  NETWORK_DEVICE_OFFLINE: [
    'device.name',
    'device.kind',
    'device.serial',
    'device.model',
    'device.site',
    'device.ip',
    'device.place',
    'device.assetTag',
    'device.offlineMinutes',
    'device.offlineSince',
    'device.lastSeen',
  ],
};

export const FREQUENCY_LABEL: Record<RecurrenceFrequency, string> = { DAILY: 'Daily', WEEKLY: 'Weekly', MONTHLY: 'Monthly', YEARLY: 'Yearly' };
export const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// ---------------------------------------------------------------------------
// Conditions
// ---------------------------------------------------------------------------

export type FieldType = 'enum' | 'priority' | 'text' | 'boolean' | 'user' | 'department' | 'reference' | 'number' | 'list';
export type OptionSource =
  | 'status'
  | 'category'
  | 'source'
  | 'personType'
  | 'commentBy'
  | 'assetCategory'
  | 'assetModel'
  | 'building'
  | 'software'
  | 'softwareProblemType'
  | 'updateField';

export interface FieldDef {
  label: string;
  type: FieldType;
  group: string;
  options?: OptionSource;
  triggers?: WorkflowTrigger[];
}

export const CONDITION_FIELDS: Record<string, FieldDef> = {
  status: { label: 'Status', type: 'enum', options: 'status', group: 'Ticket' },
  priority: { label: 'Priority', type: 'priority', group: 'Ticket' },
  category: { label: 'Type of problem', type: 'enum', options: 'category', group: 'Ticket' },
  source: { label: 'Opened by', type: 'enum', options: 'source', group: 'Ticket' },
  text: { label: 'Summary or details', type: 'text', group: 'Ticket' },
  title: { label: 'Summary', type: 'text', group: 'Ticket' },
  description: { label: 'Details', type: 'text', group: 'Ticket' },
  department: { label: 'Department', type: 'department', group: 'Routing' },
  assignee: { label: 'Assigned to', type: 'user', group: 'Routing' },
  escalated: { label: 'Has been escalated', type: 'boolean', group: 'Routing' },
  requesterType: { label: 'Requester type', type: 'enum', options: 'personType', group: 'Requester' },
  requesterEmail: { label: 'Requester email', type: 'text', group: 'Requester' },
  hasDevice: { label: 'Is about a device', type: 'boolean', group: 'Device' },
  assetCategory: { label: 'Device category', type: 'reference', options: 'assetCategory', group: 'Device' },
  assetModel: { label: 'Device model', type: 'reference', options: 'assetModel', group: 'Device' },
  building: { label: 'Building', type: 'reference', options: 'building', group: 'Device' },
  repairNeeded: { label: 'Physical repair needed', type: 'boolean', group: 'Device' },
  software: { label: 'Software', type: 'reference', options: 'software', group: 'Software' },
  softwareProblemType: { label: 'Software problem type', type: 'reference', options: 'softwareProblemType', group: 'Software' },
  deviceTicketsLast90Days: { label: 'Other tickets for the device in 90 days', type: 'number', group: 'History' },
  possibleDuplicate: { label: 'Looks like a duplicate', type: 'boolean', group: 'History' },
  openedDuringBusinessHours: { label: 'Opened during business hours', type: 'boolean', group: 'History' },
  changedFields: { label: 'What changed', type: 'list', options: 'updateField', group: 'This update', triggers: ['TICKET_UPDATED'] },
  commentBy: { label: 'Comment is from', type: 'enum', options: 'commentBy', group: 'This comment', triggers: ['COMMENT_ADDED'] },
  commentInternal: { label: 'Comment is an internal note', type: 'boolean', group: 'This comment', triggers: ['COMMENT_ADDED'] },
};

export const OPERATORS: Record<FieldType, { value: string; label: string }[]> = {
  enum: [
    { value: 'in', label: 'is any of' },
    { value: 'not_in', label: 'is none of' },
  ],
  priority: [
    { value: 'at_least', label: 'is at least' },
    { value: 'at_most', label: 'is at most' },
    { value: 'is', label: 'is' },
    { value: 'is_not', label: 'is not' },
  ],
  text: [
    { value: 'contains_any', label: 'contains any of' },
    { value: 'contains', label: 'contains' },
    { value: 'not_contains', label: 'doesn’t contain' },
    { value: 'equals', label: 'is exactly' },
    { value: 'starts_with', label: 'starts with' },
    { value: 'ends_with', label: 'ends with' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'isn’t empty' },
  ],
  boolean: [
    { value: 'is_true', label: 'is yes' },
    { value: 'is_false', label: 'is no' },
  ],
  user: [
    { value: 'is_empty', label: 'is nobody' },
    { value: 'is_not_empty', label: 'is someone' },
    { value: 'in', label: 'is any of' },
    { value: 'not_in', label: 'is none of' },
  ],
  department: [
    { value: 'within', label: 'is in or under' },
    { value: 'not_within', label: 'isn’t in or under' },
    { value: 'is', label: 'is exactly' },
    { value: 'is_not', label: 'isn’t exactly' },
    { value: 'is_empty', label: 'isn’t set' },
    { value: 'is_not_empty', label: 'is set' },
  ],
  reference: [
    { value: 'in', label: 'is any of' },
    { value: 'not_in', label: 'is none of' },
    { value: 'is_empty', label: 'isn’t set' },
    { value: 'is_not_empty', label: 'is set' },
  ],
  number: [
    { value: 'at_least', label: 'is at least' },
    { value: 'at_most', label: 'is at most' },
    { value: 'equals', label: 'is exactly' },
  ],
  list: [
    { value: 'includes', label: 'includes any of' },
    { value: 'excludes', label: 'includes none of' },
  ],
};

export const NO_VALUE_OPERATORS = new Set(['is_empty', 'is_not_empty', 'is_true', 'is_false']);
const LIST_OPERATORS = new Set(['in', 'not_in', 'includes', 'excludes']);

export function defaultValue(type: FieldType, operator: string): ConditionValue {
  if (NO_VALUE_OPERATORS.has(operator)) return null;
  if (LIST_OPERATORS.has(operator)) return [];
  if (type === 'priority') return 'HIGH';
  if (type === 'number') return 1;
  return '';
}

export function defaultCondition(field = 'category'): Condition {
  const operator = OPERATORS[CONDITION_FIELDS[field].type][0].value;
  return { type: 'condition', field, operator, value: defaultValue(CONDITION_FIELDS[field].type, operator) };
}

export const emptyGroup = (match: 'all' | 'any' = 'all'): ConditionGroup => ({ type: 'group', match, children: [] });

export function countConditions(group: ConditionGroup): number {
  return group.children.reduce((sum, child) => sum + (child.type === 'group' ? countConditions(child) : 1), 0);
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export const ACTION_LABEL: Record<WorkflowActionType, string> = {
  assign: 'Assign',
  route: 'Route to a department',
  escalate: 'Escalate',
  set_status: 'Change status',
  set_priority: 'Change priority',
  watch: 'Add watchers',
  notify: 'Notify people',
  internal_note: 'Add an internal note',
  wait: 'Wait, then continue',
  run_workflow: 'Run another workflow',
};

export const ACTION_ORDER: WorkflowActionType[] = ['assign', 'route', 'escalate', 'set_status', 'set_priority', 'watch', 'notify', 'internal_note', 'wait', 'run_workflow'];

export const NOTIFY_RECIPIENT_LABEL: Record<NotifyRecipient, string> = {
  assignee: 'The assignee',
  requester: 'The requester',
  watchers: 'Watchers',
  department_members: 'Everyone in the ticket’s department',
  department_leads: 'The department’s leads',
};

export const TICKET_PLACEHOLDERS = [
  'ticket.number',
  'ticket.title',
  'ticket.status',
  'ticket.priority',
  'ticket.link',
  'requester.name',
  'requester.email',
  'assignee.name',
  'department.name',
  'device.tag',
  'device.name',
  'room.name',
];

export function defaultAction(type: WorkflowActionType): WorkflowAction {
  switch (type) {
    case 'assign':
      return { type, mode: 'round_robin', pool: 'ticket', userId: null, departmentId: null, onlyIfUnassigned: true };
    case 'route':
      return { type, departmentId: '', assign: 'round_robin' };
    case 'escalate':
      return { type, raisePriority: true, reassign: 'department_lead', userId: null, note: '' };
    case 'set_status':
      return { type, status: 'IN_PROGRESS' };
    case 'set_priority':
      return { type, priority: 'HIGH' };
    case 'watch':
      return { type, userIds: [], departmentLeads: true };
    case 'notify':
      return { type, recipients: ['assignee'], userIds: [], title: '{{ticket.number}} needs attention', message: '{{ticket.title}}' };
    case 'internal_note':
      return { type, body: '' };
    case 'wait':
      return { type, amount: 2, unit: 'hours', businessHours: true, recheck: true };
    case 'run_workflow':
      return { type, workflowId: '' };
  }
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/** Departments on the help desk the page is for (in facilities, request categories and the crews that handle them). */
export function useDepartments(enabled = true) {
  const { service } = useDesk();
  return useQuery({
    queryKey: ['departments', service],
    queryFn: () => api.get<Department[]>('/departments', { service }),
    enabled,
    staleTime: 60_000,
  });
}

/** Who can be assigned tickets on the help desk the page is for. */
export function useAssignees(enabled = true) {
  const { service } = useDesk();
  return useQuery({
    queryKey: ['work-orders', 'assignees', service],
    queryFn: () => api.get<Assignee[]>('/work-orders/assignees', { service }),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export const useWorkflows = (enabled = true) =>
  useQuery({ queryKey: ['workflows'], queryFn: () => api.get<WorkflowListItem[]>('/workflows'), enabled });

/** Indented names for department dropdowns. */
export const indentedName = (department: { depth: number; name: string }) => `${' '.repeat(department.depth * 4)}${department.name}`;
